import { useEffect, useState } from 'react'

/**
 * オンライン/オフライン状態を検知するカスタムフック
 *
 * @returns {boolean} isOnline - オンライン状態の場合 true、オフライン状態の場合 false
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isOnline } = useOnlineStatus()
 *
 *   return (
 *     <div>
 *       {isOnline ? (
 *         <span>オンライン（サーバーから取得）</span>
 *       ) : (
 *         <span>オフライン（キャッシュから表示）</span>
 *       )}
 *     </div>
 *   )
 * }
 * ```
 */
export const useOnlineStatus = (): { isOnline: boolean } => {
  // navigator.onLineの初期値を使用
  const [isOnline, setIsOnline] = useState(() => {
    // サーバーサイドレンダリング対応
    if (typeof navigator === 'undefined') return true
    return navigator.onLine
  })

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
    }

    const handleOffline = () => {
      setIsOnline(false)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return { isOnline }
}
